import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { BaseComponent } from "../../../base.component";
import { Observer, take, takeUntil } from "rxjs";
import { GestionErreursService } from "../../../services/gestion-erreurs.service";
import { FormGroup } from "@angular/forms";
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-base-form',
  template: ``,
  standalone: true
})
export class BaseFormComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('footer', { read: ElementRef, static: false })
  footer!: ElementRef<HTMLElement>;

  private keyboardShowListener: any;
  private keyboardHideListener: any;

  private isMobile = Capacitor.getPlatform() !== 'web';
  
  @Input() inPageMode: any;

  /**
   * ID utilisé pour savoir si on est en mode édition
   */
  @Input() id?: string | number;

  /**
   * Event émis après submit (success ou retour)
   */
  @Output() resSubmitEvent = new EventEmitter<any>();

  /**
   * Message d'erreur affiché dans le composant
   */
  errMessage = '';

  /**
   * Indique si une requête est en cours
   */
  pending = false;

  /**
   * Formulaire Angular
   */
  form!: FormGroup;

  /**
   * Repository pour CRUD (à injecter dans le composant enfant)
   */
  repository: any;

  /**
   * Valeurs par défaut (ex: création)
   */
  defaultValues?: Record<string, any>;

  /**
   * Valeurs forcées (ex: userId)
   */
  fixedValues?: Record<string, any>;

  constructor(
    protected translateService: TranslateService,
    protected gestionErreursService: GestionErreursService
  ) {
    super();
  }

  // =============================
  // INIT
  // =============================
  ngOnInit(getItem = true): void {
    this.form.markAsPristine();
    this.applyDefaultValues();
    this.applyFixedValues();

    // Si ID => mode édition
    if (this.id && getItem) {
      this.loadItem();
    }
  }

  ngAfterViewInit(): void {

    if (!this.isMobile || !this.footer) return;

    this.keyboardShowListener = Keyboard.addListener(
      'keyboardWillShow',
      (info: KeyboardInfo) => {
        this.footer.nativeElement.style.bottom = `${info.keyboardHeight}px`;
      }
    );

    this.keyboardHideListener = Keyboard.addListener(
      'keyboardWillHide',
      () => {
        this.footer.nativeElement.style.bottom = '0px';
      }
    );
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    if (Capacitor.getPlatform() !== 'web') {
      this.keyboardShowListener?.remove();
      this.keyboardHideListener?.remove();
    }

  }

  // =============================
  // APPLY DEFAULT VALUES
  // =============================
  private applyDefaultValues(): void {
    if (!this.defaultValues) return;
    Object.entries(this.defaultValues).forEach(([key, value]) => {
      const control = this.form.get(key);

      if (!control) {
        console.error(`Control ${key} introuvable`);
        return;
      }

      control.setValue(this.isDate(value) ? this.toDate(value) : value);
    });
  }

  // =============================
  // APPLY FIXED VALUES
  // =============================
  private applyFixedValues(): void {
    if (!this.fixedValues) return;

    Object.entries(this.fixedValues).forEach(([key, value]) => {
      const control = this.form.get(key);
      if (control) control.setValue(value);
    });
  }

  // =============================
  // LOAD ITEM (MODE EDIT)
  // =============================
  private loadItem(): void {
    this.repository.getOneById(this.id).then((req: any) => {
      req.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
        next: (res: any) => this.form.patchValue(res),
        error: (err: any) => this.gestionErreursService.handleError(err)
      });
    });
  }

  // =============================
  // DATE HELPERS
  // =============================

  /**
   * Vérifie si la valeur est une date
   */
  private isDate(value: any): boolean {
    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }

    if (typeof value === 'string') {
      // ISO ou YYYY-MM-DD
      const isoRegex = /^\d{4}-\d{2}-\d{2}/;
      return isoRegex.test(value) && !isNaN(Date.parse(value));
    }

    return false;
  }

  /**
   * Convertit en Date JS
   */
  private toDate(value: any): Date {
    return new Date(value);
  }

  // =============================
  // SUBMIT
  // =============================
  submit() : void {
    if(this.pending) return;
    this.onFormSubmit(this.form.value);
  }

  onFormSubmit(body: any): void {

    // Ajoute les valeurs fixes
    if (this.fixedValues) {
      Object.assign(body, this.fixedValues);
    }

    if (!this.form.valid) {
      this.invalidFormAlert();
      return;
    }

    this.pending = true;

    const request = this.id
      ? this.repository.updateById(this.id, body)
      : this.repository.create(body);

    request.then((req: any) => {
      req.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (res: any) => this.submitFormSuccessEvent(res),
          error: (err: any) => this.submitFormErrorEvent(err)
        } as Observer<any>);
    });
  }

  // =============================
  // INVALID FORM ALERT
  // =============================
  protected invalidFormAlert(): void {
    this.translateService.get('forms.erreurs.INVALID_FORM')
      .pipe(take(1))
      .subscribe((res: string) => {
        this.gestionErreursService.handleError({
          error: { message: res }
        });
      });
  }

  // =============================
  // SUCCESS
  // =============================
  protected submitFormSuccessEvent(res: any): void {
    this.pending = false;
    this.errMessage = '';
    this.form.markAsPristine();
    this.resSubmitEvent.emit(res);
  }

  // =============================
  // ERROR
  // =============================
  protected submitFormErrorEvent(err: any): void {
    this.pending = false;
    this.gestionErreursService.handleError(err);
  }

  // =============================
  // BACK ACTION
  // =============================
  previous(): void {
    this.resSubmitEvent.emit(false);
    if(this.inPageMode) {
      this.ngOnInit()
    }
  }
}